import { cva, type VariantProps } from 'class-variance-authority';
import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from 'react';

import { cn } from '../../lib/cn';

/**
 * A button whose whole label is an icon — which means the label has to come
 * from somewhere else, so `label` is required rather than optional.
 *
 * The bake-off measured detail-panel close buttons at 17–18 px across all three
 * directions. That is the icon, not the target: the smallest size here is 32 px
 * (the desktop pointer floor) and worker surfaces use `lg`, which is 48.
 */
const iconButton = cva(
  [
    'inline-flex shrink-0 items-center justify-center',
    'rounded-control border',
    'cursor-pointer select-none',
    'transition-colors dur-state ease-out',
    'disabled:cursor-not-allowed disabled:border-transparent',
    'disabled:bg-surface-sunk disabled:text-ink-disabled',
  ],
  {
    variants: {
      variant: {
        primary: 'border-transparent bg-action text-action-fg hover:bg-action-hover active:bg-action-press',
        secondary: 'border-border-field bg-surface text-ink hover:bg-surface-sunk',
        quiet: 'border-transparent bg-transparent text-ink-muted hover:bg-surface-sunk hover:text-ink',
        /** For icon buttons sitting on the slate chassis (sidebar, top rail). */
        chassis:
          'border-transparent bg-transparent text-ink-inverse hover:bg-chassis-raised focus-visible:outline-focus-inverse',
        danger:
          'border-transparent bg-transparent text-status-danger-ink hover:bg-status-danger-surface',
      },
      size: {
        /** 32 px — the desktop pointer floor. Never on a worker surface. */
        sm: 'size-8',
        /** 40 px — the admin default, matches `Button` size md. */
        md: 'size-10',
        /** 48 px — the worker floor. */
        lg: 'size-touch',
      },
    },
    defaultVariants: { variant: 'quiet', size: 'md' },
  },
);

export type IconButtonVariants = VariantProps<typeof iconButton>;

export interface IconButtonProps
  extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'color' | 'aria-label'>,
    IconButtonVariants {
  /** The accessible name. Required, already translated by the caller. */
  readonly label: string;
  /** The icon. Rendered decoratively — `label` is what is announced. */
  readonly children: ReactNode;
}

export const IconButton = forwardRef<HTMLButtonElement, IconButtonProps>(function IconButton(
  { className, variant, size, label, children, type = 'button', ...rest },
  ref,
) {
  return (
    <button
      {...rest}
      ref={ref}
      type={type}
      aria-label={label}
      className={cn(iconButton({ variant, size }), className)}
    >
      <span aria-hidden="true" className="inline-flex">
        {children}
      </span>
    </button>
  );
});
